import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getSharePointSites, SharePointSite } from '../services/api';

export const MAX_SHAREPOINT_SITES = 10;

interface SharePointSitesPickerProps {
  sites: SharePointSite[];
  onChange: (sites: SharePointSite[]) => void;
}

const FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const ACCENT = '#0f766e';
const ACCENT_BG = '#ccfbf1';
const ACCENT_BORDER = '#14b8a6';

// Rows are keyed by URL, so a repeated URL would make React attach checkboxes to the wrong rows.
function uniqueByUrl(sites: SharePointSite[]): SharePointSite[] {
  const seen = new Set<string>();

  return sites.filter((site) => {
    const key = site.url.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);

    return true;
  });
}

const SharePointSitesPicker: React.FC<SharePointSitesPickerProps> = ({ sites, onChange }) => {
  const { colors } = useTheme();
  const { getAccessToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<SharePointSite[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    inputRef.current?.focus();
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Debounced so typing a site name doesn't fire a Graph search per keystroke.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoadingOptions(true);
      setError(null);
      try {
        // Refreshes the shared auth header, which otherwise expires while the page stays open.
        await getAccessToken();
        const found = await getSharePointSites(query.trim() || undefined);
        if (!cancelled) setOptions(uniqueByUrl(Array.isArray(found) ? found : []));
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        if (!cancelled) {
          setOptions([]);
          setError(typeof detail?.code === 'string' && detail.code.startsWith('sharepoint_')
            ? detail.message
            : 'Could not load SharePoint sites.');
        }
      } finally {
        if (!cancelled) setIsLoadingOptions(false);
      }
    }, query ? 300 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, query, getAccessToken]);

  const isSelected = (site: SharePointSite) => sites.some((s) => s.url === site.url);
  const atLimit = sites.length >= MAX_SHAREPOINT_SITES;
  // Selected sites stay listed so they can be unticked after the search text changes.
  const listed = [...sites, ...options.filter((o) => !isSelected(o))];

  const toggleSite = (site: SharePointSite) => {
    if (isSelected(site)) {
      onChange(sites.filter((s) => s.url !== site.url));
    } else if (!atLimit) {
      onChange([...sites, site]);
    }
  };

  const label = sites.length === 0
    ? 'All my SharePoint'
    : sites.length === 1
      ? sites[0].name
      : `${sites.length} sites`;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title={sites.length ? `Searching only: ${sites.map((s) => s.name).join(', ')}` : 'Choose which SharePoint sites to search'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          borderRadius: '16px',
          backgroundColor: sites.length ? ACCENT_BG : colors.surface,
          border: `1px solid ${sites.length ? ACCENT_BORDER : colors.border}`,
          fontSize: '14px',
          color: sites.length ? ACCENT : colors.textSecondary,
          fontFamily: FONT,
          cursor: 'pointer',
          maxWidth: '200px',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {isOpen && (
        <div
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsOpen(false);
          }}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 12px)',
            left: 0,
            width: '340px',
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            padding: '14px',
            zIndex: 1000,
            fontFamily: FONT,
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
            Search only these sites
          </div>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px', marginBottom: '10px' }}>
            Leave empty to search all the SharePoint you can access.
          </div>

          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              // Enter would otherwise submit the chat form this picker sits in.
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder="Find a site by name"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 10px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.background,
              color: colors.textPrimary,
              fontSize: '13px',
              fontFamily: FONT,
              outline: 'none',
            }}
          />

          <div style={{ fontSize: '11px', color: colors.textMuted, margin: '10px 0 4px' }}>
            {query.trim() ? 'Matching sites' : 'Sites you follow'}
          </div>

          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {isLoadingOptions && (
              <div style={{ fontSize: '13px', color: colors.textMuted, padding: '6px 0' }}>Loading…</div>
            )}
            {!isLoadingOptions && error && (
              <div style={{ fontSize: '12px', color: '#ef4444', padding: '6px 0' }}>{error}</div>
            )}
            {!isLoadingOptions && !error && listed.length === 0 && (
              <div style={{ fontSize: '13px', color: colors.textMuted, padding: '6px 0' }}>
                {query.trim() ? 'No sites found.' : 'You are not following any sites. Search for one above.'}
              </div>
            )}
            {(isLoadingOptions || error ? sites : listed).map((site) => {
              const selected = isSelected(site);
              const disabled = !selected && atLimit;

              return (
                <label
                  key={site.url}
                  title={site.url}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 4px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: disabled ? colors.textMuted : colors.textPrimary,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input type="checkbox" checked={selected} disabled={disabled} onChange={() => toggleSite(site)} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{site.name}</span>
                </label>
              );
            })}
          </div>

          {sites.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
              <span style={{ fontSize: '11px', color: colors.textMuted }}>
                {sites.length} of {MAX_SHAREPOINT_SITES} selected
              </span>
              <button
                type="button"
                onClick={() => onChange([])}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: colors.textSecondary,
                  fontSize: '12px',
                  fontFamily: FONT,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SharePointSitesPicker;
